import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { from, of, Subscription } from 'rxjs';
import { catchError, finalize, map, mergeMap, toArray } from 'rxjs/operators';
import { BaseComponent } from '../../../base/base.component';
import { FunctionService } from '../../../services/function.service';
import { ClientsBrands } from '../../../services/shipment-management.service';
import { SearchableSelectComponent, SearchableSelectOption } from '../../../components/searchable-select/searchable-select.component';
import { DamageNavBoxComponent } from '../damage-overview/damage-nav-box/damage-nav-box.component';
import { ParcelClaimsPopupComponent } from '../damage-overview/parcel-claims-popup/parcel-claims-popup.component';
import { Damage, DamageUserRef } from '../../../interfaces/Damages.interfaces';
import {
  DamageManagementService,
  DamageClaimsSearchRequest,
  DamageClaimsSummary,
  DamageRow,
  DamageSearchRequest
} from '../../../services/damage-management.service';

/** Colonna della tabella delle pratiche */
interface ClaimColumn {
  key: keyof Damage | 'clientName';
  label: string;
  sortable: boolean;
  type: 'text' | 'date' | 'status' | 'severity' | 'type' | 'number' | 'client' | 'user';
}

/** Riga della sezione "Clienti": un cliente con il suo resoconto pratiche */
interface ClientOverviewRow {
  clientCode: string;
  clientName: string;
  brandCount: number;
  summary: DamageClaimsSummary;
  /** true se il resoconto del cliente non è stato caricato (errore backend) */
  error: boolean;
}

/** Colonna numerica della tabella clienti (chiave del resoconto) */
type ClientSortKey = 'clientName' | keyof DamageClaimsSummary;

/** Colonna della tabella dei colli danneggiati (chiavi delle righe di /damaged-parcels/search) */
interface ParcelColumn {
  key: string;
  label: string;
  sortable: boolean;
  type: 'text' | 'client' | 'date' | 'number' | 'action';
}

/**
 * /damages-client-overview: panoramica delle pratiche danno incentrata sul
 * cliente. Stessa pagina di /damage-overview (header, strumentario, filtri,
 * resoconto, tabella delle pratiche con ordinamento e paginazione lato backend,
 * endpoint /claims/search e /claims/summary) con il cliente come dimensione
 * principale:
 * - il filtro cliente è il primo e il più largo;
 * - la sezione "Clienti" mostra una riga per cliente con i conteggi per stato
 *   (un /claims/summary per cliente, sugli stessi filtri della ricerca);
 * - dalla riga del cliente si mettono a fuoco le sue pratiche nella tabella,
 *   dove la colonna Cliente è la prima;
 * - nella stessa card, un selettore passa dalle pratiche alla tabella dei
 *   colli danneggiati del cliente (endpoint /search, stesse righe di
 *   /damaged-parcels) con paginazione e ordinamento propri.
 */
@Component({
  selector: 'app-damage-client-overview',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SearchableSelectComponent, DamageNavBoxComponent, ParcelClaimsPopupComponent],
  templateUrl: './damage-client-overview.component.html',
  styleUrls: ['./damage-client-overview.component.css']
})
export class DamageClientOverviewComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    functionService: FunctionService,
    router: Router,
    private damageService: DamageManagementService
  ) {
    super(functionService, router);
    // La rotta è /damages-client-overview ma la funzione dei permessi (backend
    // DamagesController) si chiama "damages": BaseComponent la ricaverebbe dall'URL
    this.functionName = 'damages';
  }

  private readonly STORAGE_KEY = 'damage-client-overview-settings';
  /** Richieste /claims/summary per cliente eseguite in parallelo */
  private readonly CLIENT_SUMMARY_CONCURRENCY = 4;

  // ─── SEZIONI ───
  isFiltersCollapsed = false;
  isReportCollapsed  = false;
  isClientsCollapsed = false;
  isCompactTable     = false;
  /** Tabella visibile nella card in fondo: pratiche (principale) o colli danneggiati */
  activeTable: 'claims' | 'parcels' = 'claims';

  showTable(table: 'claims' | 'parcels'): void {
    this.activeTable = table;
  }

  // ─── RIQUADRO UTENTE E COLLEGAMENTI (componente condiviso nell'header) ───
  @ViewChild(DamageNavBoxComponent) navBox?: DamageNavBoxComponent;

  // ─── FILTRI ───
  filterDamageCode  = '';
  filterParcelCode  = '';
  filterWaybillCode = '';
  // Select a scelta multipla (come in /damage-overview): più valori in OR
  filterClient:   string[] = [];
  filterStatus:   string[] = [];
  filterSeverity: string[] = [];
  filterType:     string[] = [];
  filterAssignedTo  = '';
  onlyOverdue       = false;
  selectedOpenedFrom: Date | null = null;
  selectedOpenedTo:   Date | null = null;

  @ViewChild('openedFromInput') openedFromInput?: ElementRef<HTMLInputElement>;
  @ViewChild('openedToInput')   openedToInput?:   ElementRef<HTMLInputElement>;
  private fpFrom: FlatpickrInstance | null = null;
  private fpTo:   FlatpickrInstance | null = null;

  statusOptions: SearchableSelectOption[] = [
    { value: 'OPEN',        label: 'Aperta' },
    { value: 'IN_PROGRESS', label: 'In lavorazione' },
    { value: 'WAITING',     label: 'In attesa' },
    { value: 'CLOSED',      label: 'Chiusa' },
    { value: 'REJECTED',    label: 'Respinta' }
  ];
  severityOptions: SearchableSelectOption[] = [
    { value: 'LOW',      label: 'Bassa' },
    { value: 'MEDIUM',   label: 'Media' },
    { value: 'HIGH',     label: 'Alta' },
    { value: 'CRITICAL', label: 'Critica' }
  ];
  typeOptions: SearchableSelectOption[] = [
    { value: 'WATER',         label: 'Danno da acqua' },
    { value: 'BREAKAGE',      label: 'Rottura' },
    { value: 'MISSING_PARTS', label: 'Parti mancanti' },
    { value: 'PHYSICAL',      label: 'Danno fisico' },
    { value: 'CONTENT',       label: 'Contenuto' },
    { value: 'PACKAGING',     label: 'Imballaggio' },
    { value: 'MISSING',       label: 'Mancante' },
    { value: 'OTHER',         label: 'Altro' }
  ];
  clientSelectOptions: SearchableSelectOption[] = [];

  /** Clienti e brand dell'utente (userFunctions in localStorage), inviati per le autorizzazioni */
  private clientsBrands: ClientsBrands[] = [];
  private clientNameByCode = new Map<string, string>();

  // ─── RESOCONTO (della selezione corrente) ───
  summary: DamageClaimsSummary = this.emptySummary();
  isLoadingSummary    = false;
  summaryErrorMessage = '';

  // ─── CLIENTI (un resoconto per cliente) ───
  clientRows: ClientOverviewRow[] = [];
  isLoadingClients    = false;
  hasLoadedClients    = false;
  clientsErrorMessage = '';
  clientSortField: ClientSortKey = 'total';
  clientSortAsc = false;
  private clientsSubscription: Subscription | null = null;

  readonly clientColumns: { key: ClientSortKey; label: string; numeric: boolean }[] = [
    { key: 'clientName', label: 'Cliente',        numeric: false },
    { key: 'total',      label: 'Totali',         numeric: true },
    { key: 'open',       label: 'Aperte',         numeric: true },
    { key: 'inProgress', label: 'In lavorazione', numeric: true },
    { key: 'waiting',    label: 'In attesa',      numeric: true },
    { key: 'overdue',    label: 'Scadute',        numeric: true },
    { key: 'closed',     label: 'Chiuse',         numeric: true },
    { key: 'rejected',   label: 'Respinte',       numeric: true }
  ];

  // ─── TABELLA ───
  rows: Damage[] = [];
  /** Utenti citati dalle pratiche della pagina (dal backend, per userCode): per mostrare i nomi */
  users: Record<string, DamageUserRef> = {};
  isLoading         = false;
  hasLoaded         = false;
  tableErrorMessage = '';

  sortField = 'damageOpenedAt';
  sortAsc   = false;

  currentPage  = 1;
  itemsPerPage = 25;
  totalRecords = 0;

  /** Come in /damage-overview, ma con il cliente per primo */
  readonly columns: ClaimColumn[] = [
    { key: 'clientName',        label: 'Cliente',     sortable: true, type: 'client' },
    { key: 'damageCode',        label: 'Pratica',     sortable: true, type: 'text' },
    { key: 'damageParcelCode',  label: 'Collo',       sortable: true, type: 'text' },
    { key: 'damageWaybillCode', label: 'Bolla',       sortable: true, type: 'text' },
    { key: 'damageType',        label: 'Tipo',        sortable: true, type: 'type' },
    { key: 'damageStatus',      label: 'Stato',       sortable: true, type: 'status' },
    { key: 'damageSeverity',    label: 'Gravità',     sortable: true, type: 'severity' },
    { key: 'damagePriority',    label: 'Priorità',    sortable: true, type: 'number' },
    { key: 'damageAssignedTo',  label: 'Assegnata a', sortable: true, type: 'user' },
    { key: 'damageOpenedAt',    label: 'Aperta il',   sortable: true, type: 'date' },
    { key: 'damageDueDate',     label: 'Scadenza',    sortable: true, type: 'date' }
  ];

  // ─── COLLI DANNEGGIATI (tabella sotto le pratiche, endpoint /search) ───
  parcelRows: DamageRow[] = [];
  isLoadingParcels    = false;
  hasLoadedParcels    = false;
  parcelsErrorMessage = '';
  parcelSortField = 'date';
  parcelSortAsc   = false;
  parcelPage         = 1;
  parcelTotalRecords = 0;
  /** Collo di cui mostrare lo storico pratiche nel popup (null = popup chiuso) */
  claimsPopupRow: DamageRow | null = null;

  /**
   * Come le colonne di default di /damaged-parcels, con il cliente per primo.
   * Ordinabili solo le chiavi previste dal SORT_FIELD_MAP del backend
   * (quantity è la chiave che il backend mappa su parcelWeight).
   */
  readonly parcelColumns: ParcelColumn[] = [
    { key: 'clientName',  label: 'Cliente',       sortable: false, type: 'client' },
    { key: 'parcelCode',  label: 'Collo',         sortable: true,  type: 'text' },
    { key: 'waybillCode', label: 'Bolla',         sortable: true,  type: 'text' },
    { key: 'date',        label: 'Data',          sortable: true,  type: 'date' },
    { key: 'quantity',    label: 'Peso (kg)',     sortable: true,  type: 'number' },
    { key: 'volume',      label: 'Volume (m³)',   sortable: false, type: 'number' },
    { key: 'damageClaim', label: 'Pratica Danno', sortable: false, type: 'action' }
  ];

  // ─── LIFECYCLE ───
  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'auto' });
    this.loadUserPreferences();
    this.loadClients();
    // Mostra subito tutti i clienti e tutte le pratiche
    this.search();
  }

  ngAfterViewInit(): void {
    this.initFlatpickr();
  }

  ngOnDestroy(): void {
    this.destroyFlatpickr();
    this.clientsSubscription?.unsubscribe();
  }

  // ─── FLATPICKR ───
  private initFlatpickr(): void {
    this.destroyFlatpickr();
    if (this.openedFromInput?.nativeElement) {
      this.fpFrom = flatpickr(this.openedFromInput.nativeElement, {
        dateFormat: 'd/m/Y H:i', enableTime: true, time_24hr: true, locale: Italian,
        defaultDate: this.selectedOpenedFrom ?? undefined,
        onChange: (dates: Date[]) => { this.selectedOpenedFrom = dates[0] ?? null; }
      });
    }
    if (this.openedToInput?.nativeElement) {
      this.fpTo = flatpickr(this.openedToInput.nativeElement, {
        dateFormat: 'd/m/Y H:i', enableTime: true, time_24hr: true, locale: Italian,
        defaultDate: this.selectedOpenedTo ?? undefined,
        onChange: (dates: Date[]) => { this.selectedOpenedTo = dates[0] ?? null; }
      });
    }
  }

  private destroyFlatpickr(): void {
    this.fpFrom?.destroy(); this.fpFrom = null;
    this.fpTo?.destroy();   this.fpTo   = null;
  }

  /** Sposta la data "dal" di n giorni (o la imposta a oggi se vuota) */
  shiftOpenedFrom(days: number): void {
    const base = this.selectedOpenedFrom ?? new Date();
    const next = new Date(base);
    next.setDate(next.getDate() + days);
    this.selectedOpenedFrom = next;
    this.fpFrom?.setDate(next, false);
  }

  setOpenedFromToday(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.selectedOpenedFrom = today;
    this.fpFrom?.setDate(today, false);
  }

  clearOpenedFrom(): void {
    this.selectedOpenedFrom = null;
    this.fpFrom?.clear();
  }

  shiftOpenedTo(days: number): void {
    const base = this.selectedOpenedTo ?? new Date();
    const next = new Date(base);
    next.setDate(next.getDate() + days);
    this.selectedOpenedTo = next;
    this.fpTo?.setDate(next, false);
  }

  setOpenedToToday(): void {
    const today = new Date();
    today.setHours(23, 59, 0, 0);
    this.selectedOpenedTo = today;
    this.fpTo?.setDate(today, false);
  }

  clearOpenedTo(): void {
    this.selectedOpenedTo = null;
    this.fpTo?.clear();
  }

  // ─── DATI INIZIALI ───
  /** Clienti da localStorage (stessa sorgente di /damage-overview e shipment-management) */
  private loadClients(): void {
    const stored = localStorage.getItem('userFunctions');
    if (stored) {
      try {
        this.clientsBrands = JSON.parse(stored)?.clients ?? [];
      } catch {
        this.clientsBrands = [];
      }
    }
    this.clientSelectOptions = this.clientsBrands
      .filter(cb => cb.clientCode && cb.clientName)
      .map(cb => ({ value: cb.clientCode, label: cb.clientName! }));
    this.clientNameByCode = new Map(
      this.clientsBrands
        .filter(cb => cb.clientCode && cb.clientName)
        .map(cb => [cb.clientCode, cb.clientName!] as [string, string])
    );
  }

  // ─── STRUMENTARIO ───
  /** Ri-esegue la ricerca con i filtri correnti, restando sulla pagina corrente */
  refreshData(): void {
    if (this.isLoading) return;
    this.navBox?.refresh();
    this.loadData();
  }

  get areAllSectionsCollapsed(): boolean {
    return this.isFiltersCollapsed && this.isReportCollapsed && this.isClientsCollapsed;
  }

  toggleAllSections(): void {
    const collapse = !this.areAllSectionsCollapsed;
    this.isFiltersCollapsed = collapse;
    this.isReportCollapsed  = collapse;
    this.isClientsCollapsed = collapse;
    if (!collapse) setTimeout(() => this.initFlatpickr());
  }

  toggleFiltersCollapsed(): void {
    this.isFiltersCollapsed = !this.isFiltersCollapsed;
    // I campi data vengono ricreati dal *ngIf: flatpickr va reinizializzato
    if (!this.isFiltersCollapsed) setTimeout(() => this.initFlatpickr());
  }

  toggleReportCollapsed(): void {
    this.isReportCollapsed = !this.isReportCollapsed;
  }

  toggleClientsCollapsed(): void {
    this.isClientsCollapsed = !this.isClientsCollapsed;
  }

  toggleCompactTable(): void {
    this.isCompactTable = !this.isCompactTable;
    this.saveUserPreferences();
  }

  private loadUserPreferences(): void {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      const settings = JSON.parse(raw);
      if (typeof settings.isCompactTable === 'boolean') {
        this.isCompactTable = settings.isCompactTable;
      }
      if (typeof settings.itemsPerPage === 'number' && settings.itemsPerPage > 0) {
        this.itemsPerPage = settings.itemsPerPage;
      }
    } catch (error) {
      console.warn('Error loading damage client overview preferences:', error);
    }
  }

  private saveUserPreferences(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
        isCompactTable: this.isCompactTable,
        itemsPerPage:   this.itemsPerPage
      }));
    } catch (error) {
      console.warn('Error saving damage client overview preferences:', error);
    }
  }

  // ─── RICERCA ───
  search(): void {
    this.currentPage = 1;
    this.parcelPage  = 1;
    this.loadData();
  }

  private buildSearchRequest(): DamageClaimsSearchRequest {
    const request: DamageClaimsSearchRequest = {
      page:      this.currentPage - 1,
      size:      this.itemsPerPage,
      sortField: this.sortField,
      sortAsc:   this.sortAsc
    };
    if (this.filterDamageCode.trim())  request.damageCode  = this.filterDamageCode.trim();
    if (this.filterParcelCode.trim())  request.parcelCode  = this.filterParcelCode.trim();
    if (this.filterWaybillCode.trim()) request.waybillCode = this.filterWaybillCode.trim();
    if (this.filterStatus.length)      request.statuses    = [...this.filterStatus];
    if (this.filterSeverity.length)    request.severities  = [...this.filterSeverity];
    if (this.filterType.length)        request.types       = [...this.filterType];
    if (this.filterClient.length)      request.clientCodes = [...this.filterClient];
    if (this.filterAssignedTo.trim())  request.assignedTo  = this.filterAssignedTo.trim();
    if (this.selectedOpenedFrom)       request.openedFrom  = this.selectedOpenedFrom.toISOString();
    if (this.selectedOpenedTo)         request.openedTo    = this.selectedOpenedTo.toISOString();
    if (this.onlyOverdue)              request.onlyOverdue = true;
    return request;
  }

  private loadData(): void {
    this.loadSummary();
    this.loadClientRows();
    this.loadClaims();
    this.loadParcels();
  }

  /** Pagina corrente delle pratiche (filtri, ordinamento e paginazione lato backend) */
  private loadClaims(): void {
    this.isLoading         = true;
    this.tableErrorMessage = '';
    this.damageService.searchClaims(this.buildSearchRequest(), this.clientsBrands).pipe(
      finalize(() => { this.isLoading = false; this.hasLoaded = true; })
    ).subscribe({
      next: res => {
        this.rows         = res.content;
        this.users        = res.users;
        this.totalRecords = res.totalElements;
      },
      error: () => {
        this.rows              = [];
        this.users             = {};
        this.totalRecords      = 0;
        this.tableErrorMessage = 'Errore nel caricamento delle pratiche. Riprova più tardi.';
      }
    });
  }

  /** Resoconto sugli stessi filtri della ricerca, così le cifre corrispondono all'elenco */
  private loadSummary(): void {
    this.isLoadingSummary    = true;
    this.summaryErrorMessage = '';
    this.damageService.getClaimsSummary(this.buildSearchRequest(), this.clientsBrands).pipe(
      finalize(() => { this.isLoadingSummary = false; })
    ).subscribe({
      next: summary => { this.summary = summary; },
      error: () => {
        this.summary             = this.emptySummary();
        this.summaryErrorMessage = 'Errore nel caricamento del resoconto.';
      }
    });
  }

  retrySummary(): void {
    if (this.isLoadingSummary) return;
    this.loadSummary();
  }

  private emptySummary(): DamageClaimsSummary {
    return { total: 0, open: 0, inProgress: 0, waiting: 0, closed: 0, rejected: 0, overdue: 0 };
  }

  // ─── CLIENTI ───
  /** Clienti su cui costruire la sezione: quelli filtrati, altrimenti tutti quelli dell'utente */
  private get clientsInScope(): ClientsBrands[] {
    const all = this.clientsBrands.filter(cb => cb.clientCode);
    if (!this.filterClient.length) return all;
    const wanted = new Set(this.filterClient);
    return all.filter(cb => wanted.has(cb.clientCode));
  }

  /**
   * Un /claims/summary per cliente, sugli stessi filtri della ricerca (il filtro
   * cliente viene sostituito dal singolo cliente). Le richieste partono a
   * gruppi di CLIENT_SUMMARY_CONCURRENCY; un errore su un cliente non blocca
   * gli altri, la riga resta con error = true.
   */
  private loadClientRows(): void {
    this.clientsSubscription?.unsubscribe();
    const clients = this.clientsInScope;
    this.clientsErrorMessage = '';
    if (clients.length === 0) {
      this.clientRows       = [];
      this.hasLoadedClients = true;
      return;
    }
    this.isLoadingClients = true;
    const baseRequest = this.buildSearchRequest();
    this.clientsSubscription = from(clients).pipe(
      mergeMap(client => {
        const request: DamageClaimsSearchRequest = { ...baseRequest, clientCodes: [client.clientCode] };
        return this.damageService.getClaimsSummary(request, this.clientsBrands).pipe(
          map((summary): ClientOverviewRow => this.clientRow(client, summary, false)),
          catchError(() => of(this.clientRow(client, this.emptySummary(), true)))
        );
      }, this.CLIENT_SUMMARY_CONCURRENCY),
      toArray(),
      finalize(() => { this.isLoadingClients = false; this.hasLoadedClients = true; })
    ).subscribe({
      next: rows => {
        this.clientRows = rows;
        this.sortClientRows();
        if (rows.length > 0 && rows.every(r => r.error)) {
          this.clientsErrorMessage = 'Errore nel caricamento dei resoconti per cliente.';
        }
      },
      error: () => {
        this.clientRows          = [];
        this.clientsErrorMessage = 'Errore nel caricamento dei resoconti per cliente.';
      }
    });
  }

  private clientRow(client: ClientsBrands, summary: DamageClaimsSummary, error: boolean): ClientOverviewRow {
    return {
      clientCode: client.clientCode,
      clientName: client.clientName?.trim() || client.clientCode,
      brandCount: Array.isArray(client.brands) ? client.brands.length : 0,
      summary,
      error
    };
  }

  retryClients(): void {
    if (this.isLoadingClients) return;
    this.loadClientRows();
  }

  /** Ordinamento lato client della tabella clienti (i dati sono già tutti in pagina) */
  sortClientsBy(key: ClientSortKey): void {
    if (this.clientSortField === key) { this.clientSortAsc = !this.clientSortAsc; }
    else { this.clientSortField = key; this.clientSortAsc = key === 'clientName'; }
    this.sortClientRows();
  }

  private sortClientRows(): void {
    const key = this.clientSortField;
    const dir = this.clientSortAsc ? 1 : -1;
    this.clientRows = [...this.clientRows].sort((a, b) => {
      if (key === 'clientName') return a.clientName.localeCompare(b.clientName, 'it') * dir;
      const diff = (a.summary[key] - b.summary[key]) * dir;
      return diff !== 0 ? diff : a.clientName.localeCompare(b.clientName, 'it');
    });
  }

  getClientSortIcon(key: ClientSortKey): string {
    if (this.clientSortField !== key) return 'fas fa-sort text-muted';
    return this.clientSortAsc ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
  }

  /** Cliente messo a fuoco: unico cliente nel filtro */
  get focusedClientCode(): string | null {
    return this.filterClient.length === 1 ? this.filterClient[0] : null;
  }

  get focusedClientName(): string | null {
    const code = this.focusedClientCode;
    return code ? (this.clientNameByCode.get(code) ?? code) : null;
  }

  isFocusedClient(row: ClientOverviewRow): boolean {
    return row.clientCode === this.focusedClientCode;
  }

  /** Mette a fuoco un cliente: filtro cliente = solo lui, poi ricerca (un secondo click toglie il fuoco) */
  focusClient(row: ClientOverviewRow): void {
    this.filterClient = this.isFocusedClient(row) ? [] : [row.clientCode];
    this.search();
  }

  clearClientFocus(): void {
    if (!this.filterClient.length) return;
    this.filterClient = [];
    this.search();
  }

  /** Totali della tabella clienti (somma delle righe caricate) */
  get clientTotals(): DamageClaimsSummary {
    return this.clientRows.reduce((acc, row) => ({
      total:      acc.total      + row.summary.total,
      open:       acc.open       + row.summary.open,
      inProgress: acc.inProgress + row.summary.inProgress,
      waiting:    acc.waiting    + row.summary.waiting,
      closed:     acc.closed     + row.summary.closed,
      rejected:   acc.rejected   + row.summary.rejected,
      overdue:    acc.overdue    + row.summary.overdue
    }), this.emptySummary());
  }

  trackByClientRow(index: number, row: ClientOverviewRow): string {
    return row.clientCode;
  }

  resetFilters(): void {
    this.filterDamageCode  = '';
    this.filterParcelCode  = '';
    this.filterWaybillCode = '';
    this.filterClient      = [];
    this.filterStatus      = [];
    this.filterSeverity    = [];
    this.filterType        = [];
    this.filterAssignedTo  = '';
    this.onlyOverdue       = false;
    this.selectedOpenedFrom = null;
    this.selectedOpenedTo   = null;
    this.fpFrom?.clear();
    this.fpTo?.clear();
    this.sortField = 'damageOpenedAt';
    this.sortAsc   = false;
    this.search();
  }

  // ─── ORDINAMENTO (lato backend) ───
  /** Campo di ordinamento inviato al backend per una colonna (il cliente è ordinato per codice) */
  private sortKeyOf(col: ClaimColumn): string {
    return col.key === 'clientName' ? 'damageClientCode' : col.key;
  }

  sortBy(col: ClaimColumn): void {
    if (!col.sortable) return;
    const field = this.sortKeyOf(col);
    if (this.sortField === field) { this.sortAsc = !this.sortAsc; }
    else { this.sortField = field; this.sortAsc = true; }
    this.loadClaims();
  }

  getSortIcon(col: ClaimColumn): string {
    if (this.sortField !== this.sortKeyOf(col)) return 'fas fa-sort text-muted';
    return this.sortAsc ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
  }

  // ─── PAGINAZIONE (stessa barra di /damage-overview) ───
  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.itemsPerPage));
  }

  /** Numeri di pagina mostrati nella barra (finestra di 5 attorno alla corrente) */
  private pageNumbersFor(current: number, totalPages: number): number[] {
    const maxButtons = 5;
    let start = Math.max(1, current - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  getPageItems(): (number | null)[] {
    return this.pageItemsFor(this.currentPage, this.totalPages);
  }

  /** Numeri di pagina con le pagine estreme e i puntini (null) quando ce ne sono altre oltre la finestra */
  private pageItemsFor(current: number, total: number): (number | null)[] {
    const window = this.pageNumbersFor(current, total);
    if (window.length === 0) return [];
    const items: (number | null)[] = [];
    const first = window[0];
    const last = window[window.length - 1];
    if (first > 1) {
      items.push(1);
      if (first > 2) items.push(null);
    }
    items.push(...window);
    if (last < total) {
      if (last < total - 1) items.push(null);
      items.push(total);
    }
    return items;
  }

  trackByPageItem(index: number, item: number | null): number {
    return item ?? -index - 1;
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadClaims();
  }

  // Vai a pagina
  isPageJumpOpen = false;
  pageJumpValue = '';
  @ViewChild('pageJumpInput') pageJumpInput?: ElementRef<HTMLInputElement>;
  // Vai all'elemento
  isItemJumpOpen = false;
  itemJumpValue = '';
  @ViewChild('itemJumpInput') itemJumpInput?: ElementRef<HTMLInputElement>;

  openPageJump(): void {
    this.pageJumpValue = '';
    this.isPageJumpOpen = true;
    setTimeout(() => this.pageJumpInput?.nativeElement.focus());
  }

  closePageJump(): void {
    this.isPageJumpOpen = false;
  }

  applyPageJump(): void {
    const n = parseInt(this.pageJumpValue, 10);
    this.closePageJump();
    if (!isNaN(n)) {
      this.onPageChange(Math.min(Math.max(n, 1), this.totalPages));
    }
  }

  openItemJump(): void {
    this.itemJumpValue = '';
    this.isItemJumpOpen = true;
    setTimeout(() => this.itemJumpInput?.nativeElement.focus());
  }

  closeItemJump(): void {
    this.isItemJumpOpen = false;
  }

  applyItemJump(): void {
    const n = parseInt(this.itemJumpValue, 10);
    this.closeItemJump();
    if (!isNaN(n)) {
      const item = Math.min(Math.max(n, 1), Math.max(1, this.totalRecords));
      this.onPageChange(Math.ceil(item / this.itemsPerPage));
    }
  }

  /** Elementi per pagina condivisi dalle tabelle Pratiche e Colli */
  setItemsPerPage(n: number): void {
    if (this.itemsPerPage === n) return;
    this.itemsPerPage = n;
    this.currentPage  = 1;
    this.parcelPage   = 1;
    this.saveUserPreferences();
    this.loadClaims();
    this.loadParcels();
  }

  // ─── COLLI DANNEGGIATI ───
  /**
   * Filtri della ricerca tradotti per /damaged-parcels/search: cliente, codici
   * collo e bolla sono gli stessi; lo stato pratica passa solo se ne è scelto
   * uno (il backend ne accetta uno solo). Le date di apertura pratica,
   * gravità, tipo, assegnatario e scadenza non hanno un equivalente sui colli
   * e non vengono applicati.
   */
  private buildParcelSearchRequest(): DamageSearchRequest {
    const request: DamageSearchRequest = {
      clientCode:    this.focusedClientCode,
      warehouseCode: null,
      dateFrom:      null,
      dateTo:        null,
      page:          this.parcelPage - 1,
      size:          this.itemsPerPage,
      sortField:     this.parcelSortField,
      sortAsc:       this.parcelSortAsc
    };
    if (this.filterClient.length)      request.clientCodes = [...this.filterClient];
    if (this.filterParcelCode.trim())  request.parcelCode  = this.filterParcelCode.trim();
    if (this.filterWaybillCode.trim()) request.waybillCode = this.filterWaybillCode.trim();
    if (this.filterStatus.length === 1) request.claimStatus = this.filterStatus[0];
    return request;
  }

  /** Pagina corrente dei colli danneggiati (paginazione e ordinamento lato backend) */
  private loadParcels(): void {
    this.isLoadingParcels    = true;
    this.parcelsErrorMessage = '';
    this.damageService.search(this.buildParcelSearchRequest(), this.clientsBrands).pipe(
      finalize(() => { this.isLoadingParcels = false; this.hasLoadedParcels = true; })
    ).subscribe({
      next: res => {
        this.parcelRows         = res.content;
        this.parcelTotalRecords = res.totalElements;
      },
      error: () => {
        this.parcelRows          = [];
        this.parcelTotalRecords  = 0;
        this.parcelsErrorMessage = 'Errore nel caricamento dei colli danneggiati. Riprova più tardi.';
      }
    });
  }

  retryParcels(): void {
    if (this.isLoadingParcels) return;
    this.loadParcels();
  }

  sortParcelsBy(col: ParcelColumn): void {
    if (!col.sortable) return;
    if (this.parcelSortField === col.key) { this.parcelSortAsc = !this.parcelSortAsc; }
    else { this.parcelSortField = col.key; this.parcelSortAsc = true; }
    this.loadParcels();
  }

  getParcelSortIcon(col: ParcelColumn): string {
    if (this.parcelSortField !== col.key) return 'fas fa-sort text-muted';
    return this.parcelSortAsc ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
  }

  get parcelTotalPages(): number {
    return Math.max(1, Math.ceil(this.parcelTotalRecords / this.itemsPerPage));
  }

  getParcelPageItems(): (number | null)[] {
    return this.pageItemsFor(this.parcelPage, this.parcelTotalPages);
  }

  get parcelRangeStart(): number {
    return this.parcelTotalRecords === 0 ? 0 : (this.parcelPage - 1) * this.itemsPerPage + 1;
  }

  get parcelRangeEnd(): number {
    return Math.min(this.parcelPage * this.itemsPerPage, this.parcelTotalRecords);
  }

  onParcelPageChange(page: number): void {
    if (page < 1 || page > this.parcelTotalPages || page === this.parcelPage) return;
    this.parcelPage = page;
    this.loadParcels();
  }

  parcelRowNumber(index: number): number {
    return (this.parcelPage - 1) * this.itemsPerPage + index + 1;
  }

  parcelClientName(row: DamageRow): string {
    const code = (row['clientCode'] ?? '') as string;
    return this.clientNameByCode.get(code) ?? code;
  }

  /** Tutte le pratiche del collo (storico), dalla più recente */
  claimsOf(row: DamageRow): Damage[] {
    return Array.isArray(row['damageClaims']) ? row['damageClaims'] : [];
  }

  /** Collo senza pratiche: apre la creazione di una nuova pratica danno */
  openNewClaim(row: DamageRow, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/damage-overview/create', row['parcelCode'] || '']);
  }

  /** Collo con una sola pratica (aperta o chiusa): apre direttamente il suo dettaglio */
  openSingleClaim(row: DamageRow, event?: Event): void {
    event?.stopPropagation();
    const claim = this.claimsOf(row)[0];
    if (claim?.damageId != null) {
      this.router.navigate(['/damage-overview/detail', Number(claim.damageId)]);
    }
  }

  /** Riga del collo: una pratica apre il dettaglio, più pratiche aprono il popup, nessuna la creazione */
  openParcel(row: DamageRow): void {
    const count = this.claimsOf(row).length;
    if (count === 1) this.openSingleClaim(row);
    else if (count > 1) this.openClaimsPopup(row);
    else this.openNewClaim(row);
  }

  openClaimsPopup(row: DamageRow, event?: Event): void {
    event?.stopPropagation();
    this.claimsPopupRow = row;
  }

  closeClaimsPopup(): void {
    this.claimsPopupRow = null;
  }

  /** Dal popup: apre il dettaglio della pratica scelta */
  openClaimFromPopup(damageId: number): void {
    this.claimsPopupRow = null;
    this.router.navigate(['/damage-overview/detail', damageId]);
  }

  trackByParcel(index: number, row: DamageRow): string {
    return (row['parcelCode'] as string) || String(index);
  }

  // ─── RIGHE ───
  openDetail(damage: Damage): void {
    this.router.navigate(['/damage-overview/detail', Number(damage.damageId)]);
  }

  isClosed(damage: Damage): boolean {
    return damage.damageStatus === 'CLOSED' || damage.damageStatus === 'REJECTED';
  }

  isOverdue(damage: Damage): boolean {
    if (this.isClosed(damage) || !damage.damageDueDate) return false;
    const due = new Date(damage.damageDueDate);
    return !isNaN(due.getTime()) && due.getTime() < Date.now();
  }

  /** Numero progressivo della riga nell'intero elenco (non solo nella pagina) */
  rowNumber(index: number): number {
    return (this.currentPage - 1) * this.itemsPerPage + index + 1;
  }

  /** Valore di una colonna data (stringa ISO dal backend), null se vuoto */
  dateValue(damage: Damage, col: ClaimColumn): string | null {
    const v = this.cellValue(damage, col);
    return typeof v === 'string' && v ? v : null;
  }

  /** Valore grezzo della cella (il nome cliente è risolto a parte) */
  cellValue(damage: Damage, col: ClaimColumn): unknown {
    return col.key === 'clientName' ? this.clientName(damage) : damage[col.key];
  }

  clientName(damage: Damage): string {
    const code = damage.damageClientCode ?? '';
    return this.clientNameByCode.get(code) ?? code;
  }

  /**
   * Nome dell'utente dal suo codice (mappa `users` della pagina); se il
   * backend non lo ha risolto resta il codice. Null se il codice è vuoto.
   */
  userName(userCode?: string | null): string | null {
    if (!userCode) return null;
    const ref = this.users[userCode] ?? this.users[userCode.trim()];
    return ref?.userName?.trim() || userCode;
  }

  statusBadgeClass(status?: string): string {
    switch (status) {
      case 'OPEN':        return 'dmg-badge--info';
      case 'IN_PROGRESS': return 'dmg-badge--pending';
      case 'WAITING':     return 'dmg-badge--medium';
      case 'CLOSED':      return 'dmg-badge--resolved';
      case 'REJECTED':    return 'dmg-badge--critical';
      default:            return 'dmg-badge--info';
    }
  }

  severityBadgeClass(severity?: string): string {
    switch (severity) {
      case 'CRITICAL': return 'dmg-badge--critical';
      case 'HIGH':     return 'dmg-badge--high';
      case 'MEDIUM':   return 'dmg-badge--medium';
      case 'LOW':      return 'dmg-badge--low';
      default:         return 'dmg-badge--info';
    }
  }

  trackByDamage(index: number, damage: Damage): number {
    return damage.damageId ?? index;
  }
}
